"use client";

// nurani-allow: unvalidated-body the flagged read is the error field off the
// same-origin /api/contact response, rendered as React text (auto-escaped, no
// HTML sink); browser-side code reads no inbound request body.

import { useEffect, useRef, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";

export function ContactPage() {
  const t = useTranslations("contact");
  const tCommon = useTranslations("common");
  const locale = useLocale();

  // Anti-spam fill-time marker, set on mount via effect. Date.now() in
  // render (or in a state initializer) would bake the static prerender's
  // build time into the page and permanently defeat the server's
  // minimum-fill-time check.
  const renderedAt = useRef(0);
  useEffect(() => {
    renderedAt.current = Date.now();
  }, []);

  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // _honeypot rides along in `data` (hidden input); _timestamp + _token
        // complete the kit verifyAntiSpam trio checked server-side.
        body: JSON.stringify({
          ...data,
          locale,
          _timestamp: renderedAt.current,
          _token: "js-on",
        }),
      });
      if (res.ok) {
        form.reset();
        setSent(true);
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.error || t("errorMessage"));
      }
    } catch {
      setError(t("connectionError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <a href={`/${locale}`} className="hover:text-foreground transition-colors">{tCommon("home")}</a>
            <span>/</span>
            <span className="text-text-secondary">{t("title")}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Info */}
            <div>
              <p className="label-sm mb-3">{t("title")}</p>
              <h1 className="heading-lg mb-6">{t("subtitle")}</h1>
              <p className="text-text-body text-[15px] leading-relaxed mb-10">
                {t("text")} {t("responseTime")}
              </p>

              {/* Allergy advice highlight */}
              <div className="bg-surface border border-border p-6 mb-10 rounded-2xl">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-cta shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8.5l3.5 3.5 6.5-8" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">
                      {t("allergyAdviceTitle")}
                    </h3>
                    <p className="text-sm text-text-body leading-relaxed">
                      {t("allergyAdvice")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact methods */}
              <div className="space-y-6">
                <div>
                  <p className="label-sm mb-2">{t("email")}</p>
                  <a href="mailto:info@unitycosmetics.com" className="text-foreground hover:text-text-secondary transition-colors">
                    info@unitycosmetics.com
                  </a>
                </div>
                <div>
                  <p className="label-sm mb-2">{t("whatsapp")}</p>
                  <a href="https://wa.me/31657222000" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-text-secondary transition-colors">
                    +31 (0)6 57 222 000
                  </a>
                </div>
                <div>
                  <p className="label-sm mb-2">{t("availability")}</p>
                  <p className="text-sm text-text-body">{t("availabilityText")}</p>
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div>
              {sent ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="bg-surface border border-border rounded-2xl p-8 lg:p-10 text-center"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-cta/15 mb-5">
                    <svg className="w-7 h-7 text-cta" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8.5l3.5 3.5 6.5-8" />
                    </svg>
                  </div>
                  <p className="text-[17px] text-foreground font-medium mb-2 leading-snug">
                    {t("successMessage")}
                  </p>
                  <p className="text-sm text-text-tertiary">
                    {t("responseTime")}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot field - hidden from humans. Named _honeypot to
                      match the kit verifyAntiSpam default field name. */}
                  <input type="text" name="_honeypot" className="hidden" tabIndex={-1} autoComplete="off" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="label-sm mb-1.5 block">{t("firstName")}</label>
                      <input
                        type="text" name="firstName" id="firstName"
                        required
                        className="w-full h-11 px-4 bg-surface border border-border text-foreground text-sm outline-none focus:border-foreground/40 transition-colors rounded-xl"
                        style={{ transitionDuration: "var(--duration-fast)" }}
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="label-sm mb-1.5 block">{t("lastName")}</label>
                      <input
                        type="text" name="lastName" id="lastName"
                        required
                        className="w-full h-11 px-4 bg-surface border border-border text-foreground text-sm outline-none focus:border-foreground/40 transition-colors rounded-xl"
                        style={{ transitionDuration: "var(--duration-fast)" }}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="label-sm mb-1.5 block">{t("emailField")}</label>
                    <input
                      type="email" name="email" id="email"
                      required
                      className="w-full h-11 px-4 bg-surface border border-border text-foreground text-sm outline-none focus:border-foreground/40 transition-colors"
                      style={{ transitionDuration: "var(--duration-fast)" }}
                    />
                  </div>
                  <div>
                    <label className="label-sm mb-1.5 block">{t("subject")}</label>
                    <select
                      name="subject"
                      className="w-full h-11 px-4 bg-surface border border-border text-foreground text-sm outline-none focus:border-foreground/40 transition-colors rounded-xl"
                      style={{ transitionDuration: "var(--duration-fast)" }}
                    >
                      <option value="">{t("subjectPlaceholder")}</option>
                      <option value="product">{t("subjectProduct")}</option>
                      <option value="allergy">{t("subjectAllergy")}</option>
                      <option value="order">{t("subjectOrder")}</option>
                      <option value="return">{t("subjectReturn")}</option>
                      <option value="other">{t("subjectOther")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-sm mb-1.5 block">{t("message")}</label>
                    <textarea
                      name="message"
                      rows={6}
                      required
                      className="w-full px-4 py-3 bg-surface border border-border text-foreground text-sm outline-none focus:border-foreground/40 transition-colors resize-none rounded-xl"
                      style={{ transitionDuration: "var(--duration-fast)" }}
                      placeholder={t("messagePlaceholder")}
                    />
                  </div>
                  {error && (
                    <div
                      role="alert"
                      className="flex items-start gap-2.5 bg-negative/10 border border-negative/30 text-negative text-sm px-4 py-3 rounded-xl"
                    >
                      <svg className="w-4 h-4 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 9v4M12 17v.01M3 12a9 9 0 1018 0 9 9 0 00-18 0z" />
                      </svg>
                      <span>{error}</span>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
                  >
                    {submitting && (
                      <span className="w-4 h-4 border-2 border-background/40 border-t-background rounded-full animate-spin" aria-hidden />
                    )}
                    {submitting ? t("sending") : t("send")}
                  </button>
                  <p className="text-xs text-text-tertiary text-center">
                    {t("responseTime")}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
